/**
 * shared/pmove/ground.ts — PM_GroundTrace, PM_CrashLand (landing + fall damage), all-solid fix.
 *
 * OWNER: PMOVE agent.
 * T6 GroundTrace 0x9428c0 / 0x943bd0 [PM §2]; the body is [T5 bg_pmove.cpp:PM_GroundTrace,
 * PM_CorrectAllSolid, PM_GroundTraceMissed, PM_CrashLand]. T6's fall-damage function (candidate
 * 0x944bc0, PM §9) is not decompiled: PM_CrashLand follows T5 with bg_fallDamageMinHeight /
 * MaxHeight from ctx.dvars (ESTIMATE 128 / 300) [MOVPUB §1.5].
 */
import { CONTENTS, createTraceResult, SURF } from '../collision/types';
import { ENTITYNUM_NONE, ENTITYNUM_WORLD } from '../constants';
import type { Vec3 } from '../math';
import { PEv, PMF, PmType } from '../types';
import { groundSurfaceType } from './footsteps';
import { jumpClearState } from './jump';
import { addTouchEnt, copyTrace, pmTrace, traceWalkable, type Pm } from './pm';

/** CorrectSolidDeltas [T5 bg_pmove.cpp:138]: nudges tried when stuck in a solid. */
const CORRECT_SOLID_DELTAS: readonly (readonly [number, number, number])[] = [
  [0, 0, 1], [-1, 0, 1], [0, -1, 1], [1, 0, 1], [0, 1, 1], [-1, 0, 0], [0, -1, 0], [1, 0, 0], [0, 1, 0],
  [0, 0, -1], [-1, 0, -1], [0, -1, -1], [1, 0, -1], [0, 1, -1], [-1, -1, 1], [1, -1, 1], [1, 1, 1], [-1, 1, 1],
  [-1, -1, 0], [1, -1, 0], [1, 1, 0], [-1, 1, 0], [-1, -1, -1], [1, -1, -1], [1, 1, -1], [-1, 1, -1],
];

const tr = createTraceResult();
const tr2 = createTraceResult();
const start: Vec3 = [0, 0, 0];
const point: Vec3 = [0, 0, 0];

/** Landing severities in PEv.LAND parm bits 8-9 (types.ts). */
export const LandSeverity = { LIGHT: 0, MEDIUM: 1, HARD: 2, DAMAGE: 3 } as const;

/** Ground entity of a trace (Trace_GetEntityHitId). */
function hitEntity(t: { entityNum: number }): number {
  return t.entityNum === ENTITYNUM_NONE ? ENTITYNUM_WORLD : t.entityNum;
}

/** PM_CorrectAllSolid [T5 bg_pmove.cpp:3671]. */
function correctAllSolid(pm: Pm): boolean {
  const ps = pm.ps;
  for (const d of CORRECT_SOLID_DELTAS) {
    point[0] = ps.origin[0] + d[0]; point[1] = ps.origin[1] + d[1]; point[2] = ps.origin[2] + d[2];
    pmTrace(pm, tr2, point, pm.mins, pm.maxs, point);
    if (!tr2.startsolid) {
      ps.origin[0] = point[0]; ps.origin[1] = point[1]; ps.origin[2] = point[2];
      point[2] = ps.origin[2] - 1 - 0.25;
      pmTrace(pm, tr2, ps.origin, pm.mins, pm.maxs, point);
      copyTrace(pm.groundTrace, tr2);
      ps.origin[0] = tr2.endpos[0]; ps.origin[1] = tr2.endpos[1]; ps.origin[2] = tr2.endpos[2];
      copyTrace(tr, tr2);
      return true;
    }
  }
  ps.groundEntityNum = hitEntity(tr);
  pm.groundPlane = false;
  pm.almostGroundPlane = false;
  pm.walking = false;
  jumpClearState(ps);
  return false;
}

/**
 * Un-penetrate: T6's slide solver (0x6feaa0) runs a second pass that pushes the hull out of
 * planes it overlaps [PM §3 PM_SlideMove step 6]; the Q3-style slide we port has no such pass,
 * so a hull that starts a frame inside world geometry (4 Hijacked mp_dm_spawns overlap clip
 * brushes by up to 7 u, see collision/hijacked.test.ts) is moved to the nearest free spot along
 * the CorrectSolidDeltas directions, 1..48 u away. Other players never count (they can be
 * walked out of). APPROXIMATION of the T6 solver's un-penetration.
 */
const UNSTICK_STEPS = [1, 2, 3, 4, 6, 8, 10, 12, 16, 20, 24, 32, 40, 48];
export function unstick(pm: Pm): void {
  const ps = pm.ps;
  const mask = pm.tracemask & ~CONTENTS.BODY;
  pmTrace(pm, tr2, ps.origin, pm.mins, pm.maxs, ps.origin, mask);
  if (!tr2.startsolid) return;
  for (const k of UNSTICK_STEPS) {
    for (const d of CORRECT_SOLID_DELTAS) {
      point[0] = ps.origin[0] + d[0] * k; point[1] = ps.origin[1] + d[1] * k; point[2] = ps.origin[2] + d[2] * k;
      pmTrace(pm, tr2, point, pm.mins, pm.maxs, point, mask);
      if (!tr2.startsolid) {
        ps.origin[0] = point[0]; ps.origin[1] = point[1]; ps.origin[2] = point[2];
        return;
      }
    }
  }
}

/** PM_GroundTraceMissed [T5 bg_pmove.cpp:3708]. */
function groundTraceMissed(pm: Pm): void {
  const ps = pm.ps;
  if (ps.groundEntityNum === ENTITYNUM_NONE) {
    point[0] = ps.origin[0]; point[1] = ps.origin[1]; point[2] = ps.origin[2] - 1;
    pmTrace(pm, tr2, ps.origin, pm.mins, pm.maxs, point);
    pm.almostGroundPlane = tr2.fraction !== 1;
  } else {
    point[0] = ps.origin[0]; point[1] = ps.origin[1]; point[2] = ps.origin[2] - 64;
    pmTrace(pm, tr2, ps.origin, pm.mins, pm.maxs, point);
    pm.almostGroundPlane = tr2.fraction !== 1 && tr2.fraction < 0.015625;
  }
  ps.groundEntityNum = ENTITYNUM_NONE;
  pm.groundPlane = false;
  pm.walking = false;
}

/** PM_GroundTrace [T5 bg_pmove.cpp:3261]: 0.25 up to 0.25 down. */
export function groundTrace(pm: Pm): void {
  const ps = pm.ps;
  start[0] = ps.origin[0]; start[1] = ps.origin[1]; start[2] = ps.origin[2] + 0.25;
  point[0] = ps.origin[0]; point[1] = ps.origin[1]; point[2] = ps.origin[2] - 0.25;
  pmTrace(pm, tr, start, pm.mins, pm.maxs, point);
  copyTrace(pm.groundTrace, tr);
  if (tr.allsolid && !correctAllSolid(pm)) return;
  if (tr.startsolid) {
    start[2] = ps.origin[2] - 0.001;
    pmTrace(pm, tr, start, pm.mins, pm.maxs, point);
    if (tr.startsolid) {
      ps.groundEntityNum = hitEntity(tr);
      pm.groundPlane = true;
      pm.almostGroundPlane = true;
      pm.walking = traceWalkable(pm, tr);
      return;
    }
    copyTrace(pm.groundTrace, tr);
  }
  if (tr.fraction === 1) {
    groundTraceMissed(pm);
    return;
  }
  // moving up fast away from the surface: still in the air (Q3 "kicked off the ground")
  const vz = ps.velocity[2];
  if (!(ps.pm_flags & PMF.LADDER) && vz > 0
    && ps.velocity[0] * tr.normal[0] + ps.velocity[1] * tr.normal[1] + vz * tr.normal[2] > 10
    && (ps.groundEntityNum === ENTITYNUM_NONE || ps.groundEntityNum === ENTITYNUM_WORLD)) {
    pm.almostGroundPlane = false;
    ps.groundEntityNum = ENTITYNUM_NONE;
    pm.groundPlane = false;
    pm.walking = false;
    return;
  }
  if (traceWalkable(pm, tr)) {
    // DEVIATION (robustness): an origin resting exactly on (or up to 0.25 u into) the floor — e.g.
    // a spawn point placed on the surface — counts as all-solid for every hull trace (Q3 clip
    // rules), which the engine never hits because its positions always stay 0.125 off planes.
    // Lift it onto the trace's stop position (<= 0.25 u). No effect on normal movement.
    if (tr.endpos[2] - ps.origin[2] > 0.001) ps.origin[2] = tr.endpos[2];
    pm.groundPlane = true;
    pm.almostGroundPlane = true;
    pm.walking = true;
    if (ps.groundEntityNum === ENTITYNUM_NONE) crashLand(pm);
    ps.groundEntityNum = hitEntity(tr);
    addTouchEnt(pm, ps.groundEntityNum);
  } else {
    // steep slope: in the air, but sliding on a plane
    ps.groundEntityNum = ENTITYNUM_NONE;
    pm.groundPlane = true;
    pm.almostGroundPlane = true;
    pm.walking = false;
    jumpClearState(ps);
  }
}

/**
 * PM_CrashLand — T6 0x941610 (extracted/t6mp-re/dec/pmove_a.c:7598-7760; PM §9's 0x944bc0 is the
 * lean code, not this). Same shape as [T5 bg_pmove.cpp:3443]. Solves the frame's fall exactly for
 * the landing velocity, fallHeight = v^2/(2g); damage = (h-min)/(max-min)*100 (0..100); landing
 * events and slowdown:
 *  - h > 4: light, >= 8: medium, >= 12: hard (velocity x0.67, :7712), damage >= 100, slick or
 *    sliding (pm_flags 0x40000000 = PMF.SLIDE, :7721): x0.67,
 *    other damage: stun 35*dmg+500 ms (<= 2000) with PMF_TIME_SLOWDOWN, velocity x0.5..0.2.
 *  - view dip (h-12)*0.03846154*4+4 (<= 24) for the camera, carried in the LAND parm (bits 10..15).
 *  - water: no landing at all at waterlevel 4 (:7618); damage halved at waterlevel > 1 (:7675).
 * NOT ported (no effect in SnS as built): the perk / ps+0x548 bit 14 "no fall damage" zeroing
 * (:7658-7665) and the FUN_00668ee0(4)/FUN_00457ce0(ps,0x19) clamp of damage to 1 (:7667-7670).
 * Fall damage is reported as PEv.FALL_DAMAGE (parm = damage); the server applies it
 * (MOD_FALLING) — pmove never touches health.
 */
export function crashLand(pm: Pm): void {
  const ps = pm.ps;
  if (ps.waterlevel === 4) return; // T6 pmove_a.c:7618
  const dist = pm.previousOrigin[2] - ps.origin[2];
  const vel = pm.previousVelocity[2];
  const acc = -ps.gravity;
  const a = acc * 0.5;
  const den = vel * vel - 4 * a * dist;
  if (den < 0 || a === 0) return;
  const t = (-vel - Math.sqrt(den)) / (2 * a);
  const landVel = -(t * acc + vel);
  const fallHeight = (landVel * landVel) / (ps.gravity * 2);
  const dv = pm.ctx.dvars;
  let damage = 0;
  if (dv.fallDamageMinHeight < fallHeight) {
    if (fallHeight < dv.fallDamageMaxHeight) {
      damage = Math.trunc(((fallHeight - dv.fallDamageMinHeight) / (dv.fallDamageMaxHeight - dv.fallDamageMinHeight)) * 100);
      damage = damage < 100 ? (damage > 0 ? damage : 0) : 100;
    } else {
      damage = 100;
    }
  }
  if ((pm.groundTrace.surfaceFlags & 1) || ps.pm_type >= PmType.FREEZE) damage = 0; // sflags 1 = no fall damage
  if (ps.waterlevel > 1) damage = Math.trunc(damage * 0.5); // T6 pmove_a.c:7675
  let viewDip = 0;
  if (fallHeight > 12) {
    viewDip = Math.trunc(((fallHeight - 12) / 26) * 4 + 4);
    if (viewDip > 24) viewDip = 24;
  }
  const surfaceType = groundSurfaceType(pm);
  const v = ps.velocity;
  if (damage) {
    if (damage >= 100 || (ps.pm_flags & PMF.SLIDE) || (pm.groundTrace.surfaceFlags & SURF.SLICK)) {
      v[0] *= 0.67000002; v[1] *= 0.67000002; v[2] *= 0.67000002;
    } else {
      let stunTime = 35 * damage + 500;
      if (stunTime > 2000) stunTime = 2000;
      let fSpeedMult = 0.5;
      if (stunTime > 500) fSpeedMult = stunTime < 1500 ? 0.5 - ((stunTime - 500) / 1000) * 0.30000001 : 0.2;
      ps.pm_time = stunTime;
      ps.pm_flags |= PMF.TIME_SLOWDOWN;
      v[0] *= fSpeedMult; v[1] *= fSpeedMult; v[2] *= fSpeedMult;
    }
    pm.ctx.addEvent(ps, PEv.LAND, landParm(surfaceType, LandSeverity.DAMAGE, viewDip));
    pm.ctx.addEvent(ps, PEv.FALL_DAMAGE, damage);
  } else if (fallHeight > 4) {
    if (fallHeight >= 8) {
      if (fallHeight >= 12) {
        v[0] *= 0.67000002; v[1] *= 0.67000002; v[2] *= 0.67000002;
        pm.ctx.addEvent(ps, PEv.LAND, landParm(surfaceType, LandSeverity.HARD, viewDip));
      } else {
        pm.ctx.addEvent(ps, PEv.LAND, landParm(surfaceType, LandSeverity.MEDIUM, viewDip));
      }
    } else {
      pm.ctx.addEvent(ps, PEv.LAND, landParm(surfaceType, LandSeverity.LIGHT, viewDip));
    }
  }
}

/** PEv.LAND parm: surfaceType | severity << 8 | viewDip << 10 (viewDip 0..24). */
export function landParm(surfaceType: number, severity: number, viewDip: number): number {
  return (surfaceType & 0xff) | ((severity & 3) << 8) | ((viewDip & 0x3f) << 10);
}
